import { ColumnSelector } from '@shared/components/ui/ColumnSelector';
import { FiltersDropdown } from '@shared/components/ui/FiltersDropdown';
import { Search } from '@shared/components/ui/Search';
import { ColumnDef } from '@tanstack/react-table';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClientsFiltersPanel } from './ClientsFiltersPanel';

interface FiltersSectionProps {
  searchTerm: string;
  onSearch: (value: string) => void;
  customerOrigins: string[];
  onCustomerOriginsChange: (origins: string[]) => void;
  columns: ColumnDef<any>[];
  selectedColumns: string[];
  onColumnsChange: (columns: string[]) => void;
}

export const FiltersSection = ({
  searchTerm,
  onSearch,
  customerOrigins,
  onCustomerOriginsChange,
  columns,
  selectedColumns,
  onColumnsChange,
}: FiltersSectionProps) => {
  const { t } = useTranslation('clientsModule');

  const [inputValue, setInputValue] = useState(searchTerm);

  useEffect(() => {
    setInputValue((prev) => (prev === searchTerm ? prev : searchTerm));
  }, [searchTerm]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleSearchSubmit = useCallback(() => {
    onSearch(inputValue);
  }, [inputValue, onSearch]);

  const activeFiltersCount = useMemo(() => customerOrigins.length, [customerOrigins]);

  return (
    <div className="flex items-center gap-3 flex-wrap">
      <Search
        placeholder={t('filters.name_placeholder', 'Buscar por nombre...')}
        value={inputValue}
        onChange={handleInputChange}
        onEnter={handleSearchSubmit}
        className="w-64"
        dataTestId="clients-search"
        inputDataTestId="clients-search-input"
      />

      <FiltersDropdown
        title={t('filters.title', 'Filtros')}
        triggerLabel={t('filters.title', 'Filtros')}
        activeFiltersCount={activeFiltersCount}
        triggerTestId="clients-filters-toggle-button"
        dropdownContentClassName="sm:min-w-[500px] sm:max-w-[500px] p-4 bg-body-bg text-foreground border border-card-border"
      >
        <ClientsFiltersPanel customerOrigins={customerOrigins} onCustomerOriginsChange={onCustomerOriginsChange} />
      </FiltersDropdown>

      <ColumnSelector
        columns={columns}
        selected={selectedColumns}
        onColumnsChange={onColumnsChange}
        fixedColumns={['name']}
        storageKey="clients-table"
      />
    </div>
  );
};
